import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  Zap,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { AccountPosition, PositionRecommendation } from '../types';
import { apiService } from '../services/api';
import PositionItem from '../components/PositionItem';
import RecommendationBatch from '../components/RecommendationBatch';
import { getPositionTypeIcon, getRankIcon } from '../components/PositionIcons';
import {
  formatCurrency,
  formatPercentage,
  groupRecommendationsByBatch,
  getRankColor,
  getStatusColor,
  getPriorityColor,
  isExpired,
} from '../utils/positionUtils';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import { useTierPortfolio } from '../hooks/useTierPortfolio';
import {
  groupPositionsByTier,
  positionMatchesTierFilter,
} from '../tiers/resolveTierContext';
import TierChip from '../components/portfolio/TierChip';
import { ALL_RISK_TIER_NUMBERS } from '../tiers/tierCatalog';

interface PositionApprovalsProps {}

const PositionApprovals: React.FC<PositionApprovalsProps> = () => {
  const [accounts, setAccounts] = useState<
    Array<{ id: string; nickname: string; accountNumber: string }>
  >([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [positions, setPositions] = useState<AccountPosition[]>([]);
  const [recommendations, setRecommendations] = useState<
    PositionRecommendation[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState<string | null>(null);
  const [orchestrating, setOrchestrating] = useState(false);
  const [viewMode, setViewMode] = useState<'positions' | 'recommendations'>(
    'positions',
  );
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [showExpired, setShowExpired] = useState(false);
  const { accounts: portfolioAccounts, tierFilter } = useTierPortfolio();
  const accountMeta = useMemo(
    () => portfolioAccounts.find((a) => a.id === selectedAccount),
    [portfolioAccounts, selectedAccount],
  );

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const accountsData = await apiService.getAccounts();
      setAccounts(accountsData);
      if (accountsData.length > 0) {
        setSelectedAccount(accountsData[0].id);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadAccountData = async (accountId: string) => {
    if (!accountId) return;

    setLoading(true);
    try {
      const [positionsData, recommendationsData] = await Promise.all([
        apiService.getAccountPositions(accountId),
        apiService.getPositionRecommendations(accountId),
      ]);

      setPositions(positionsData.positions);
      setRecommendations(recommendationsData.recommendations);
    } catch (error) {
      console.error('Error loading account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    positionId: string,
    newStatus: string,
    notes?: string,
  ) => {
    setUpdating(positionId);
    try {
      await apiService.updatePositionStatus(positionId, newStatus, notes);
      await loadAccountData(selectedAccount);
    } catch (error) {
      console.error('Error updating position status:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleCancelPosition = async (positionId: string) => {
    setUpdating(positionId);
    try {
      await apiService.cancelPosition(positionId);
      await loadAccountData(selectedAccount);
    } catch (error) {
      console.error('Error cancelling position:', error);
    } finally {
      setUpdating(null);
    }
  };

  const runOrchestration = async () => {
    if (!selectedAccount) return;

    setOrchestrating(true);
    try {
      await apiService.orchestrateAccountPositions(selectedAccount);
      await loadAccountData(selectedAccount);
    } catch (error) {
      console.error('Error running orchestration:', error);
    } finally {
      setOrchestrating(false);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      loadAccountData(selectedAccount);
    }
  }, [selectedAccount]);

  const tierScoped = (list: AccountPosition[]) =>
    list.filter((p) =>
      positionMatchesTierFilter(p, accountMeta, tierFilter),
    );

  // Group positions by status
  const recommendedPositions = tierScoped(
    positions.filter((p) => p.status === 'RECOMMENDED'),
  );
  const activePendingPositions = tierScoped(
    positions.filter((p) =>
      [
        'APPROVED',
        'ENTRY_PENDING',
        'ACTIVE',
        'CLOSING',
        'PENDING_MARKET_OPEN',
      ].includes(p.status),
    ),
  );
  const closedCancelledExpiredPositions = tierScoped(
    positions.filter((p) =>
      [
        'CLOSED',
        'CANCELLED',
        'REJECTED',
        'EXPIRED_TIMEOUT',
        'CANCELLED_PRICE_MOVEMENT',
        'ORDERS_REJECTED',
      ].includes(p.status),
    ),
  );
  const recommendedByTier = useMemo(
    () => groupPositionsByTier(recommendedPositions, accountMeta),
    [recommendedPositions, accountMeta],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Approvals by tier"
        description="Recommendations and pending trades respect each tier's capital envelope and orchestration policy."
      />
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Position Approvals
          </h1>
          <p className="text-gray-600">
            Review and approve recommended trading positions
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => selectedAccount && loadAccountData(selectedAccount)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={runOrchestration}
            disabled={orchestrating}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {orchestrating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            {orchestrating ? 'Running...' : 'Run Orchestration'}
          </button>
        </div>
      </div>

      {/* Account Selection */}
      <div className="bg-white rounded-lg shadow p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Account
        </label>
        <select
          value={selectedAccount}
          onChange={(e) => setSelectedAccount(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {accounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.nickname} ({account.accountNumber})
            </option>
          ))}
        </select>
      </div>

      {/* View Mode Toggle */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              View Mode
            </label>
            <div className="flex border border-gray-300 rounded-lg">
              <button
                onClick={() => setViewMode('positions')}
                className={`px-3 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                  viewMode === 'positions'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Positions
              </button>
              <button
                onClick={() => setViewMode('recommendations')}
                className={`px-3 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                  viewMode === 'recommendations'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Recommendations
              </button>
            </div>
          </div>

          {viewMode === 'positions' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status Filter
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="RECOMMENDED">Recommended</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="FILLED">Filled</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showExpired}
                    onChange={(e) => setShowExpired(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Show Expired</span>
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content List */}
      {viewMode === 'positions' ? (
        // Positions View with grouped sections
        <div className="space-y-6">
          {/* Recommended Positions Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Recommended Positions ({recommendedPositions.length})
                  </h2>
                </div>
                <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  Ready for Review
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                New positions ready for approval and execution
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {ALL_RISK_TIER_NUMBERS.map((n) => {
                  const count = recommendedByTier[n].length;
                  if (!count) return null;
                  return (
                    <TierChip key={n} riskTier={n} label={`${count} recommended`} />
                  );
                })}
              </div>
            </div>

            {recommendedPositions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No recommended positions found.</p>
                <p className="text-sm mt-2">
                  Try running orchestration to generate new positions.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {recommendedPositions.map((position) => (
                  <PositionItem
                    key={position._id}
                    position={position}
                    updating={updating}
                    getPositionTypeIcon={getPositionTypeIcon}
                    getRankIcon={getRankIcon}
                    getRankColor={getRankColor}
                    getStatusColor={getStatusColor}
                    getPriorityColor={getPriorityColor}
                    isExpired={isExpired}
                    formatCurrency={formatCurrency}
                    formatPercentage={formatPercentage}
                    handleStatusUpdate={handleStatusUpdate}
                    handleCancelPosition={handleCancelPosition}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Active and Pending Positions Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Clock className="w-6 h-6 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Active & Pending Positions ({activePendingPositions.length})
                  </h2>
                </div>
                <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">
                  In Progress
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Positions currently being executed or awaiting market conditions
              </p>
            </div>

            {activePendingPositions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No active or pending positions.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {activePendingPositions.map((position) => (
                  <div key={position._id} className="p-6">
                    {/* Additional Active/Pending Position Information */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Position Performance
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-100">
                              <th className="text-left py-2 px-3 font-medium text-gray-700">
                                SYMBOL
                              </th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">
                                TYPE
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                QUANTITY
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                AVG PRICE
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                MARKET VALUE
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                COST BASIS
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                UNREALIZED P&L
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                P&L %
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-3 px-3">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {position.symbol}
                                    {position.targetHolding
                                      ?.optionExpirationDate &&
                                    position.targetHolding
                                      ?.optionStrikePrice ? (
                                      <span className="text-xs text-gray-500 ml-1">
                                        {position.targetHolding
                                          ?.optionExpirationDate instanceof Date
                                          ? position.targetHolding.optionExpirationDate
                                              .toISOString()
                                              .split('T')[0]
                                          : position.targetHolding
                                              ?.optionExpirationDate}{' '}
                                        $
                                        {
                                          position.targetHolding
                                            ?.optionStrikePrice
                                        }{' '}
                                        {
                                          position.targetHolding
                                            ?.optionContractType
                                        }
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {position.targetHolding
                                      ?.optionExpirationDate &&
                                    position.targetHolding?.optionStrikePrice
                                      ? `${position.symbol} ${
                                          position.targetHolding
                                            ?.optionExpirationDate instanceof
                                          Date
                                            ? position.targetHolding.optionExpirationDate
                                                .toISOString()
                                                .split('T')[0]
                                            : position.targetHolding
                                                ?.optionExpirationDate
                                        } $${
                                          position.targetHolding
                                            ?.optionStrikePrice
                                        } ${
                                          position.targetHolding
                                            ?.optionContractType
                                        }`
                                      : position.symbol}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-3">
                                <div className="flex flex-wrap gap-1">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {position.tradeDirection}
                                  </span>
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      position.targetHolding
                                        ?.optionExpirationDate ||
                                      position.targetHolding
                                        ?.optionStrikePrice ||
                                      position.targetHolding?.optionContractType
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                  >
                                    {position.targetHolding
                                      ?.optionExpirationDate ||
                                    position.targetHolding?.optionStrikePrice ||
                                    position.targetHolding?.optionContractType
                                      ? 'OPTION'
                                      : 'EQUITY'}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.targetHolding?.quantity || 1}
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.actualEntryPrice
                                  ? formatCurrency(position.actualEntryPrice)
                                  : position.targetHolding?.expectedFillPrice
                                    ? formatCurrency(
                                        position.targetHolding
                                          .expectedFillPrice,
                                      )
                                    : 'Pending'}
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.targetHolding?.totalValue
                                  ? formatCurrency(
                                      position.targetHolding.totalValue,
                                    )
                                  : formatCurrency(position.allocatedCapital)}
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.actualEntryPrice &&
                                position.targetHolding?.quantity
                                  ? formatCurrency(
                                      position.actualEntryPrice *
                                        position.targetHolding.quantity,
                                    )
                                  : (() => {
                                      // Calculate cost basis from available data
                                      const avgPrice =
                                        position.actualEntryPrice ||
                                        position.targetHolding
                                          ?.expectedFillPrice ||
                                        0;
                                      const quantity =
                                        position.targetHolding?.quantity || 1;
                                      // For options, multiply by 100 (contract multiplier)
                                      const isOption =
                                        position.targetHolding
                                          ?.optionExpirationDate ||
                                        position.targetHolding
                                          ?.optionStrikePrice ||
                                        position.targetHolding
                                          ?.optionContractType;
                                      const multiplier = isOption ? 100 : 1;
                                      return formatCurrency(
                                        avgPrice * quantity * multiplier,
                                      );
                                    })()}
                              </td>
                              <td
                                className={`py-3 px-3 text-right font-medium ${(() => {
                                  if (position.actualReturn !== undefined) {
                                    return position.actualReturn >= 0
                                      ? 'text-green-600'
                                      : 'text-red-600';
                                  }
                                  // Calculate P&L for color coding
                                  const avgPrice =
                                    position.actualEntryPrice ||
                                    position.targetHolding?.expectedFillPrice ||
                                    0;
                                  const quantity =
                                    position.targetHolding?.quantity || 1;
                                  // For options, multiply by 100 (contract multiplier)
                                  const isOption =
                                    position.targetHolding
                                      ?.optionExpirationDate ||
                                    position.targetHolding?.optionStrikePrice ||
                                    position.targetHolding?.optionContractType;
                                  const multiplier = isOption ? 100 : 1;
                                  const costBasis =
                                    avgPrice * quantity * multiplier;
                                  const marketValue =
                                    position.targetHolding?.totalValue ||
                                    position.allocatedCapital;
                                  const unrealizedPL = marketValue - costBasis;
                                  return unrealizedPL >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600';
                                })()}`}
                              >
                                {position.actualReturn !== undefined
                                  ? formatCurrency(
                                      position.allocatedCapital *
                                        (position.actualReturn / 100),
                                    )
                                  : (() => {
                                      // Calculate unrealized P&L from market value and cost basis
                                      const avgPrice =
                                        position.actualEntryPrice ||
                                        position.targetHolding
                                          ?.expectedFillPrice ||
                                        0;
                                      const quantity =
                                        position.targetHolding?.quantity || 1;
                                      // For options, multiply by 100 (contract multiplier)
                                      const isOption =
                                        position.targetHolding
                                          ?.optionExpirationDate ||
                                        position.targetHolding
                                          ?.optionStrikePrice ||
                                        position.targetHolding
                                          ?.optionContractType;
                                      const multiplier = isOption ? 100 : 1;
                                      const costBasis =
                                        avgPrice * quantity * multiplier;
                                      const marketValue =
                                        position.targetHolding?.totalValue ||
                                        position.allocatedCapital;
                                      const unrealizedPL =
                                        marketValue - costBasis;
                                      return formatCurrency(unrealizedPL);
                                    })()}
                              </td>
                              <td
                                className={`py-3 px-3 text-right font-medium ${(() => {
                                  if (position.actualReturn !== undefined) {
                                    return position.actualReturn >= 0
                                      ? 'text-green-600'
                                      : 'text-red-600';
                                  }
                                  // Calculate P&L percentage for color coding
                                  const avgPrice =
                                    position.actualEntryPrice ||
                                    position.targetHolding?.expectedFillPrice ||
                                    0;
                                  const quantity =
                                    position.targetHolding?.quantity || 1;
                                  // For options, multiply by 100 (contract multiplier)
                                  const isOption =
                                    position.targetHolding
                                      ?.optionExpirationDate ||
                                    position.targetHolding?.optionStrikePrice ||
                                    position.targetHolding?.optionContractType;
                                  const multiplier = isOption ? 100 : 1;
                                  const costBasis =
                                    avgPrice * quantity * multiplier;
                                  const marketValue =
                                    position.targetHolding?.totalValue ||
                                    position.allocatedCapital;
                                  const pnlPercent =
                                    costBasis > 0
                                      ? ((marketValue - costBasis) /
                                          costBasis) *
                                        100
                                      : 0;
                                  return pnlPercent >= 0
                                    ? 'text-green-600'
                                    : 'text-red-600';
                                })()}`}
                              >
                                {position.actualReturn !== undefined
                                  ? formatPercentage(position.actualReturn)
                                  : (() => {
                                      // Calculate P&L percentage from market value and cost basis
                                      const avgPrice =
                                        position.actualEntryPrice ||
                                        position.targetHolding
                                          ?.expectedFillPrice ||
                                        0;
                                      const quantity =
                                        position.targetHolding?.quantity || 1;
                                      // For options, multiply by 100 (contract multiplier)
                                      const isOption =
                                        position.targetHolding
                                          ?.optionExpirationDate ||
                                        position.targetHolding
                                          ?.optionStrikePrice ||
                                        position.targetHolding
                                          ?.optionContractType;
                                      const multiplier = isOption ? 100 : 1;
                                      const costBasis =
                                        avgPrice * quantity * multiplier;
                                      const marketValue =
                                        position.targetHolding?.totalValue ||
                                        position.allocatedCapital;
                                      const pnlPercent =
                                        costBasis > 0
                                          ? ((marketValue - costBasis) /
                                              costBasis) *
                                            100
                                          : 0;
                                      return formatPercentage(pnlPercent);
                                    })()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Cancel Position Button */}
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => handleCancelPosition(position._id)}
                          disabled={updating === position._id}
                          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updating === position._id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-2" />
                              Close Position
                            </>
                          )}
                        </button>
                      </div>

                      <PositionItem
                        position={position}
                        updating={updating}
                        getPositionTypeIcon={getPositionTypeIcon}
                        getRankIcon={getRankIcon}
                        getRankColor={getRankColor}
                        getStatusColor={getStatusColor}
                        getPriorityColor={getPriorityColor}
                        isExpired={isExpired}
                        formatCurrency={formatCurrency}
                        formatPercentage={formatPercentage}
                        handleStatusUpdate={handleStatusUpdate}
                        handleCancelPosition={handleCancelPosition}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Closed, Cancelled, and Expired Positions Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-gray-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Closed & Cancelled Positions (
                    {closedCancelledExpiredPositions.length})
                  </h2>
                </div>
                <span className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                  Completed
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Historical positions that have been closed, cancelled, or
                expired
              </p>
            </div>

            {closedCancelledExpiredPositions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No closed or cancelled positions.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {closedCancelledExpiredPositions.map((position) => (
                  <div key={position._id} className="p-6">
                    {/* Additional Closed Position Information */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Final Position Performance
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-100">
                              <th className="text-left py-2 px-3 font-medium text-gray-700">
                                SYMBOL
                              </th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">
                                TYPE
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                QUANTITY
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                ENTRY PRICE
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                EXIT PRICE
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                COST BASIS
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                REALIZED P&L
                              </th>
                              <th className="text-right py-2 px-3 font-medium text-gray-700">
                                P&L %
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-gray-100">
                              <td className="py-3 px-3">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {position.symbol}
                                    {position.targetHolding
                                      ?.optionExpirationDate &&
                                    position.targetHolding
                                      ?.optionStrikePrice ? (
                                      <span className="text-xs text-gray-500 ml-1">
                                        {position.targetHolding
                                          ?.optionExpirationDate instanceof Date
                                          ? position.targetHolding.optionExpirationDate
                                              .toISOString()
                                              .split('T')[0]
                                          : position.targetHolding
                                              ?.optionExpirationDate}{' '}
                                        $
                                        {
                                          position.targetHolding
                                            ?.optionStrikePrice
                                        }{' '}
                                        {
                                          position.targetHolding
                                            ?.optionContractType
                                        }
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {position.targetHolding
                                      ?.optionExpirationDate &&
                                    position.targetHolding?.optionStrikePrice
                                      ? `${position.symbol} ${
                                          position.targetHolding
                                            ?.optionExpirationDate instanceof
                                          Date
                                            ? position.targetHolding.optionExpirationDate
                                                .toISOString()
                                                .split('T')[0]
                                            : position.targetHolding
                                                ?.optionExpirationDate
                                        } $${
                                          position.targetHolding
                                            ?.optionStrikePrice
                                        } ${
                                          position.targetHolding
                                            ?.optionContractType
                                        }`
                                      : position.symbol}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-3">
                                <div className="flex flex-wrap gap-1">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {position.tradeDirection}
                                  </span>
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      position.targetHolding
                                        ?.optionExpirationDate ||
                                      position.targetHolding
                                        ?.optionStrikePrice ||
                                      position.targetHolding?.optionContractType
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-blue-100 text-blue-800'
                                    }`}
                                  >
                                    {position.targetHolding
                                      ?.optionExpirationDate ||
                                    position.targetHolding?.optionStrikePrice ||
                                    position.targetHolding?.optionContractType
                                      ? 'OPTION'
                                      : 'EQUITY'}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.targetHolding?.quantity || 1}
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.actualEntryPrice
                                  ? formatCurrency(position.actualEntryPrice)
                                  : (() => {
                                      // Calculate from trade history
                                      if (
                                        position.generatedTrades &&
                                        position.generatedTrades.length > 0
                                      ) {
                                        const buyTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'BUY' &&
                                              trade.status === 'FILLED',
                                          );
                                        if (
                                          buyTrade &&
                                          buyTrade.filledQuantity &&
                                          buyTrade.totalValue &&
                                          buyTrade.filledQuantity > 0
                                        ) {
                                          return formatCurrency(
                                            buyTrade.totalValue /
                                              buyTrade.filledQuantity,
                                          );
                                        }
                                      }
                                      return position.targetHolding
                                        ?.expectedFillPrice
                                        ? formatCurrency(
                                            position.targetHolding
                                              .expectedFillPrice,
                                          )
                                        : 'N/A';
                                    })()}
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.actualExitPrice
                                  ? formatCurrency(position.actualExitPrice)
                                  : (() => {
                                      // Calculate from trade history
                                      if (
                                        position.generatedTrades &&
                                        position.generatedTrades.length > 0
                                      ) {
                                        const sellTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'SELL' &&
                                              trade.status === 'FILLED',
                                          );
                                        if (
                                          sellTrade &&
                                          sellTrade.filledQuantity &&
                                          sellTrade.totalValue &&
                                          sellTrade.filledQuantity > 0
                                        ) {
                                          return formatCurrency(
                                            sellTrade.totalValue /
                                              sellTrade.filledQuantity,
                                          );
                                        }
                                      }
                                      return 'N/A';
                                    })()}
                              </td>
                              <td className="py-3 px-3 text-right font-medium">
                                {position.actualEntryPrice &&
                                position.targetHolding?.quantity
                                  ? formatCurrency(
                                      position.actualEntryPrice *
                                        position.targetHolding.quantity,
                                    )
                                  : (() => {
                                      // Calculate cost basis from trade history
                                      if (
                                        position.generatedTrades &&
                                        position.generatedTrades.length > 0
                                      ) {
                                        const buyTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'BUY' &&
                                              trade.status === 'FILLED',
                                          );
                                        if (buyTrade && buyTrade.totalValue) {
                                          return formatCurrency(
                                            buyTrade.totalValue,
                                          );
                                        }
                                      }
                                      // Fallback to calculated cost basis
                                      const avgPrice =
                                        position.actualEntryPrice ||
                                        position.targetHolding
                                          ?.expectedFillPrice ||
                                        0;
                                      const quantity =
                                        position.targetHolding?.quantity || 1;
                                      // For options, multiply by 100 (contract multiplier)
                                      const isOption =
                                        position.targetHolding
                                          ?.optionExpirationDate ||
                                        position.targetHolding
                                          ?.optionStrikePrice ||
                                        position.targetHolding
                                          ?.optionContractType;
                                      const multiplier = isOption ? 100 : 1;
                                      return formatCurrency(
                                        avgPrice * quantity * multiplier,
                                      );
                                    })()}
                              </td>
                              <td
                                className={`py-3 px-3 text-right font-medium ${(() => {
                                  // Calculate P&L from trade history
                                  if (
                                    position.generatedTrades &&
                                    position.generatedTrades.length > 0
                                  ) {
                                    const buyTrade =
                                      position.generatedTrades.find(
                                        (trade) =>
                                          trade.side === 'BUY' &&
                                          trade.status === 'FILLED',
                                      );
                                    const sellTrade =
                                      position.generatedTrades.find(
                                        (trade) =>
                                          trade.side === 'SELL' &&
                                          trade.status === 'FILLED',
                                      );
                                    if (
                                      buyTrade &&
                                      sellTrade &&
                                      buyTrade.totalValue &&
                                      sellTrade.totalValue
                                    ) {
                                      const realizedPL =
                                        sellTrade.totalValue -
                                        buyTrade.totalValue;
                                      return realizedPL >= 0
                                        ? 'text-green-600'
                                        : 'text-red-600';
                                    }
                                  }
                                  return position.actualReturn !== undefined
                                    ? position.actualReturn >= 0
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                    : 'text-gray-500';
                                })()}`}
                              >
                                {position.actualReturn !== undefined
                                  ? formatCurrency(
                                      position.allocatedCapital *
                                        (position.actualReturn / 100),
                                    )
                                  : (() => {
                                      // Calculate P&L from trade history
                                      if (
                                        position.generatedTrades &&
                                        position.generatedTrades.length > 0
                                      ) {
                                        const buyTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'BUY' &&
                                              trade.status === 'FILLED',
                                          );
                                        const sellTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'SELL' &&
                                              trade.status === 'FILLED',
                                          );
                                        if (
                                          buyTrade &&
                                          sellTrade &&
                                          buyTrade.totalValue &&
                                          sellTrade.totalValue
                                        ) {
                                          const realizedPL =
                                            sellTrade.totalValue -
                                            buyTrade.totalValue;
                                          return formatCurrency(realizedPL);
                                        }
                                      }
                                      return 'N/A';
                                    })()}
                              </td>
                              <td
                                className={`py-3 px-3 text-right font-medium ${(() => {
                                  // Calculate P&L percentage from trade history
                                  if (
                                    position.generatedTrades &&
                                    position.generatedTrades.length > 0
                                  ) {
                                    const buyTrade =
                                      position.generatedTrades.find(
                                        (trade) =>
                                          trade.side === 'BUY' &&
                                          trade.status === 'FILLED',
                                      );
                                    const sellTrade =
                                      position.generatedTrades.find(
                                        (trade) =>
                                          trade.side === 'SELL' &&
                                          trade.status === 'FILLED',
                                      );
                                    if (
                                      buyTrade &&
                                      sellTrade &&
                                      buyTrade.totalValue &&
                                      sellTrade.totalValue &&
                                      buyTrade.totalValue > 0
                                    ) {
                                      const pnlPercent =
                                        ((sellTrade.totalValue -
                                          buyTrade.totalValue) /
                                          buyTrade.totalValue) *
                                        100;
                                      return pnlPercent >= 0
                                        ? 'text-green-600'
                                        : 'text-red-600';
                                    }
                                  }
                                  return position.actualReturn !== undefined
                                    ? position.actualReturn >= 0
                                      ? 'text-green-600'
                                      : 'text-red-600'
                                    : 'text-gray-500';
                                })()}`}
                              >
                                {position.actualReturn !== undefined
                                  ? formatPercentage(position.actualReturn)
                                  : (() => {
                                      // Calculate P&L percentage from trade history
                                      if (
                                        position.generatedTrades &&
                                        position.generatedTrades.length > 0
                                      ) {
                                        const buyTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'BUY' &&
                                              trade.status === 'FILLED',
                                          );
                                        const sellTrade =
                                          position.generatedTrades.find(
                                            (trade) =>
                                              trade.side === 'SELL' &&
                                              trade.status === 'FILLED',
                                          );
                                        if (
                                          buyTrade &&
                                          sellTrade &&
                                          buyTrade.totalValue &&
                                          sellTrade.totalValue &&
                                          buyTrade.totalValue > 0
                                        ) {
                                          const pnlPercent =
                                            ((sellTrade.totalValue -
                                              buyTrade.totalValue) /
                                              buyTrade.totalValue) *
                                            100;
                                          return formatPercentage(pnlPercent);
                                        }
                                      }
                                      return 'N/A';
                                    })()}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Additional Trade Information */}
                      {position.generatedTrades &&
                        position.generatedTrades.length > 0 && (
                          <div className="mt-4">
                            <h5 className="font-medium text-gray-900 mb-2">
                              Trade History
                            </h5>
                            <div className="space-y-2">
                              {position.generatedTrades.map((trade, index) => (
                                <div
                                  key={index}
                                  className="text-xs bg-white p-2 rounded border"
                                >
                                  <div className="flex justify-between">
                                    <span className="font-medium">
                                      Order {index + 1}
                                    </span>
                                    <span
                                      className={`px-2 py-1 rounded text-xs ${
                                        trade.success
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}
                                    >
                                      {trade.status}
                                    </span>
                                  </div>
                                  <div className="text-gray-600 mt-1">
                                    {trade.message}
                                  </div>
                                  {trade.errorMessage && (
                                    <div className="text-red-600 mt-1">
                                      {trade.errorMessage}
                                    </div>
                                  )}
                                  <div className="text-gray-500 mt-1">
                                    {new Date(trade.createdAt).toLocaleString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                    <PositionItem
                      position={position}
                      updating={updating}
                      getPositionTypeIcon={getPositionTypeIcon}
                      getRankIcon={getRankIcon}
                      getRankColor={getRankColor}
                      getStatusColor={getStatusColor}
                      getPriorityColor={getPriorityColor}
                      isExpired={isExpired}
                      formatCurrency={formatCurrency}
                      formatPercentage={formatPercentage}
                      handleStatusUpdate={handleStatusUpdate}
                      handleCancelPosition={handleCancelPosition}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Recommendations View
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Recommendations ({recommendations.length})
            </h2>
          </div>

          {recommendations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No recommendations found.</p>
              <p className="text-sm mt-2">
                Try running orchestration to generate new recommendations.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {(() => {
                const groupedRecommendations =
                  groupRecommendationsByBatch(recommendations);
                return Object.entries(groupedRecommendations).map(
                  ([batchId, batchRecommendations]) => (
                    <RecommendationBatch
                      key={batchId}
                      batchId={batchId}
                      batchRecommendations={batchRecommendations}
                      getRankIcon={getRankIcon}
                      getRankColor={getRankColor}
                      getPriorityColor={getPriorityColor}
                    />
                  ),
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PositionApprovals;
